import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DetailDrawerComponent } from '@netfoundry-ui/ui/detail-drawer';
import { ElasticsearchService } from '@netfoundry-ui/shared/elasticsearch';
import { TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import { ApiService, LoggerService } from '@netfoundry-ui/shared/services';
import { parseTrafficFlows } from '@netfoundry-ui/shared/service-templates';
import type { TrafficRow } from '@netfoundry-ui/shared/model';
import {
    buildTrafficSankey,
    FlowMapChartComponent,
    flowsForLink,
    flowsForNode,
    highlightForFlows,
    type SankeyChartLink,
    type SankeyChartNode,
    type SankeyLinkClickEvent,
    type SankeyNodeClickEvent,
    type SankeyNodeToggleEvent,
    type TrafficSankeyData,
} from '@netfoundry-ui/ui/flow-map-chart';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import {
    buildCoverageGraph,
    buildSelectionFlows,
    coverageGraphFromFlows,
    explainFlows,
    flowPairKey,
    isolateSelected,
    segmentStates,
    setEndpointIncluded,
    setSourceIncluded,
    type CoverageExplain,
    type CoverageGraph,
    type CoverageRowBy,
    type CoverageServiceInput,
    type CoverageState,
    type SelectionPair,
} from './coverage-sankey.adapter';
import { CoverageDetailContentComponent, type CoverageIncludeToggle } from './coverage-detail-content.component';
import { TimeRangePillComponent, type TimeRangeOption } from '@netfoundry-ui/ui/time-range-pill';
import { DateRangeCalendarComponent } from '@netfoundry-ui/ui/date-range-calendar';

/** Everything the dialog needs: a snapshot of the service cards, the selection, and its time window. */
export interface CoverageDialogData {
    services: CoverageServiceInput[];
    pairs: SelectionPair[];
    /** Comma-joined dest addresses / ports, for the restricted ("Selected only") re-query. */
    destIp: string;
    destPort: string;
    srcIdentities: string[];
    srcIps: string[];
    hostIdentities: string[];
    /** The originating time window (epoch ms); absent on a deep link without it. */
    start?: number;
    end?: number;
    label?: string;
    coveredCount: number;
    totalSegments: number;
    /** Show a time-range selector in the dialog that re-queries the flows (for views opened without a fixed
     *  selection window, e.g. the Microsegments management page). The create flow leaves this off. */
    showTimeRange?: boolean;
}

type Mode = 'selected' | 'full';

/**
 * Modal that shows the Traffic Analysis flow-map (Sankey) colored by how much of the selected traffic the
 * in-progress service cards cover. Opens on the selected segments only (a cheap restricted re-query) and
 * can expand to the full window's traffic with everything outside the selection faded back. If the window
 * is missing or the re-query fails it degrades to a graph drawn from the selection alone.
 */
@Component({
    selector: 'app-coverage-sankey-dialog',
    templateUrl: './coverage-sankey-dialog.component.html',
    styleUrls: ['./coverage-sankey-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        FlowMapChartComponent,
        DetailDrawerComponent,
        CoverageDetailContentComponent,
        TimeRangePillComponent,
        DateRangeCalendarComponent,
    ],
})
export class CoverageSankeyDialogComponent implements OnInit, OnDestroy {
    mode: Mode = 'selected';
    loading = false;
    error = '';
    /** Whether the full-graph toggle is offered (needs the originating window and a working re-query). */
    hasWindow = false;

    /** Time-range options for the selector shown when the dialog is opened without a fixed window
     *  (`data.showTimeRange`); picking one re-queries the flows over the new window. */
    readonly rangeOptions: TimeRangeOption[] = [
        { id: '1h', label: '1h' },
        { id: '24h', label: '24h' },
        { id: '7d', label: '7d' },
        { id: 'custom', label: 'Custom' },
    ];
    rangeFilter = '24h';
    /** Whether the custom date-range calendar popover is open. */
    showCustom = false;
    /** Custom ranges are capped to the same span Traffic Analysis allows. */
    readonly rangeMaxDays = 7;
    /** True once we have fallen back to the from-selection graph (no real magnitudes, no context). */
    degraded = false;

    nodes: readonly SankeyChartNode[] = [];
    links: readonly SankeyChartLink[] = [];
    headers: readonly string[] = [];
    highlightNodeIds: readonly string[] = [];
    highlightLinkIds: readonly string[] = [];
    /** Accurate, source-aware coverage of the selected traffic (by circuits), from the fetched flows. */
    private coveredCircuits = 0;
    private selectedCircuits = 0;

    /** Which coverage states are shown in the graph. The canvas legend toggles these additively - each item
     *  is an independent show/hide; all on by default. */
    readonly visibleStates = new Set<CoverageState>(['covered', 'partial', 'uncovered']);
    /** Whether the faded "Not selected" context (full graph only) is shown; toggled by its legend item. */
    showNotSelected = true;

    /** The clicked element's coverage readout, shown in the side drawer; null when nothing is selected. */
    detail: { eyebrow: string; title: string; explain: CoverageExplain } | null = null;
    /** The open panel's source flows + context, so an include/exclude edit can recompute and reopen it. */
    private detailCtx: { eyebrow: string; title: string; flows: TrafficRow[]; rowsBy: CoverageRowBy } | null = null;
    /** Set once the viewer edits a service here, so the edits are returned to the page on close. */
    private servicesDirty = false;
    /** Source identities currently drilled into (their source-address column is shown). Keyed by the
     *  identity's node value (the part after the `1:` stage prefix). */
    private readonly expandedIdentities = new Set<string>();

    private readonly flowCache = new Map<Mode, TrafficRow[]>();
    /** The raw Sankey index (flows per node/ribbon) for the currently drawn graph, for hover/click. */
    private displayData: TrafficSankeyData = {
        nodes: [],
        links: [],
        headers: [],
        flowsByNode: new Map(),
        flowsByLink: new Map(),
        labelById: new Map(),
    };
    /** The mode's resting highlight, re-applied when a hover ends. */
    private baseHighlightNodes: readonly string[] = [];
    private baseHighlightLinks: readonly string[] = [];
    /** The endpoint column index in the shared Sankey (source address 0, identities 1-2, endpoint 3). */
    private readonly STAGE_ENDPOINT = 3;

    private readonly destroyRef = inject(DestroyRef);
    /** Keeps the scoped-overlay offsets in sync with the main content area as the sidebar collapses/expands. */
    private scopeObserver?: ResizeObserver;
    /** The dim layer dedicated to this modal, parented inside <main> so it only ever covers page content. */
    private scrimEl?: HTMLElement;

    constructor(
        public dialogRef: MatDialogRef<CoverageSankeyDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CoverageDialogData,
        private elasticsearch: ElasticsearchService,
        @Inject(TEMPLATE_SEARCH_SERVICE) private templateService: TemplateSearchService,
        private apiService: ApiService,
        private logger: LoggerService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.hasWindow = !!(this.data.start && this.data.end);
        this.load('selected');
        // The overlay is scoped to the content area, so the sidebar and header stay clickable while this is
        // open. Close on any route change so the modal never lingers over a page it no longer belongs to
        // (MatDialog's closeOnNavigation only covers browser back/forward, not router-link navigation).
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.dialogRef.close());
        // Anchor the scoped overlay to the main content area's real edges. The sidebar width changes when it
        // collapses/expands and no token tracks it, so measure <main> and keep it in sync as it resizes.
        this.applyContentScope();
        const main = document.querySelector('main');
        if (main) {
            if (typeof ResizeObserver !== 'undefined') {
                this.scopeObserver = new ResizeObserver(() => this.applyContentScope());
                this.scopeObserver.observe(main);
            }
            this.installContentScrim(main);
        }
    }

    ngOnDestroy(): void {
        this.scopeObserver?.disconnect();
        const root = document.documentElement.style;
        root.removeProperty('--cov-scope-left');
        root.removeProperty('--cov-scope-top');
        this.scrimEl?.remove();
        document.querySelector('main')?.classList.remove('cov-content-scrim-host');
    }

    /** Publish the main content area's left/top (viewport px) as CSS vars the scoped-overlay rules consume. */
    private applyContentScope(): void {
        const main = document.querySelector('main');
        if (!main) {
            return;
        }
        const rect = main.getBoundingClientRect();
        const root = document.documentElement.style;
        root.setProperty('--cov-scope-left', `${Math.max(0, Math.round(rect.left))}px`);
        root.setProperty('--cov-scope-top', `${Math.max(0, Math.round(rect.top))}px`);
    }

    /** Dim only the content area with a scrim parented inside <main> (which becomes its own stacking
     *  context), so the sidebar, header and their menus stay fully visible above it and there is no CDK
     *  backdrop to fight their z-index. Clicking the scrim closes the modal, like a backdrop would. */
    private installContentScrim(main: Element): void {
        main.classList.add('cov-content-scrim-host');
        const scrim = document.createElement('div');
        scrim.className = 'cov-content-scrim';
        scrim.addEventListener('click', () => this.dialogRef.close());
        main.appendChild(scrim);
        this.scrimEl = scrim;
    }

    get windowLabel(): string {
        if (!this.data.start || !this.data.end) return '';
        const fmt = (t: number) => moment(t).format('MMM D, HH:mm');
        return `${fmt(this.data.start)} – ${fmt(this.data.end)}`;
    }

    /** Source-aware coverage of the selected traffic by circuits, or null until flows have loaded. */
    get trafficPercent(): number | null {
        return this.selectedCircuits > 0 ? Math.round((this.coveredCircuits / this.selectedCircuits) * 100) : null;
    }

    /** True when there is nothing to draw (query returned no selected flows), once loading has settled. */
    get isEmpty(): boolean {
        return !this.loading && this.nodes.length === 0;
    }

    /** True when the current scope has flows to show (before the legend's visibility toggles are applied).
     *  Used to keep the legend reachable even when the toggles have hidden everything. */
    get hasAnyFlows(): boolean {
        return (this.flowCache.get(this.mode)?.length ?? 0) > 0;
    }

    setMode(mode: Mode): void {
        if (mode === this.mode || this.loading) return;
        this.mode = mode;
        this.load(mode);
    }

    /** Toggle a coverage state's visibility in the graph (additive show/hide). Flows are already cached, so
     *  this just re-filters. */
    toggleState(state: CoverageState): void {
        if (this.loading) return;
        if (this.visibleStates.has(state)) {
            this.visibleStates.delete(state);
        } else {
            this.visibleStates.add(state);
        }
        this.rebuild();
    }

    /** Toggle the faded "Not selected" context layer (full graph only). */
    toggleNotSelected(): void {
        if (this.loading) return;
        this.showNotSelected = !this.showNotSelected;
        this.rebuild();
    }

    /** Pick a preset range (Custom only opens the calendar); re-query the current scope. */
    onRangePreset(id: string): void {
        if (this.loading) return;
        if (id === 'custom') {
            this.showCustom = true;
            return;
        }
        this.rangeFilter = id;
        this.applyWindow(this.windowMs(id));
    }

    /** Re-clicking the already-selected Custom pill re-opens the calendar. */
    onRangeReclick(id: string): void {
        if (id === 'custom') {
            this.showCustom = true;
        }
    }

    toggleCustomRange(): void {
        this.showCustom = !this.showCustom;
    }

    onCustomRange(picked: { start: number; end: number }): void {
        this.showCustom = false;
        this.rangeFilter = 'custom';
        this.applyWindow(picked);
    }

    /** The active window as one readout, e.g. "Oct 22 - Oct 29, 2026" (matches Traffic Analysis). */
    get rangeReadout(): string {
        const start = moment(this.data.start);
        const end = moment(this.data.end);
        if (start.isSame(end, 'day')) {
            return `${start.format('MMM D, h:mm A')} – ${end.format('h:mm A')}`;
        }
        return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }

    /** Set the window, drop the cached flows, and re-query the current scope. */
    private applyWindow({ start, end }: { start: number; end: number }): void {
        this.data.start = start;
        this.data.end = end;
        this.hasWindow = true;
        this.degraded = false;
        this.flowCache.clear();
        this.load(this.mode);
    }

    /** Resolve a range preset to an epoch-ms window ending now. */
    private windowMs(preset: string): { start: number; end: number } {
        const end = moment();
        const start =
            preset === '1h'
                ? end.clone().subtract(1, 'hour')
                : preset === '7d'
                  ? end.clone().subtract(7, 'days')
                  : end.clone().subtract(24, 'hours');
        return { start: start.valueOf(), end: end.valueOf() };
    }

    close(): void {
        // Hand the edited service snapshot back to the page so it can apply the include/exclude changes.
        this.dialogRef.close(this.servicesDirty ? this.data.services : undefined);
    }

    private async load(mode: Mode): Promise<void> {
        this.loading = true;
        this.error = '';
        try {
            await this.flowsFor(mode); // fetch + cache the flows for this scope
            this.rebuild();
        } catch (error) {
            this.logger?.error?.(error);
            if (mode === 'full') {
                // Keep the selected view intact; just report that the context graph could not load.
                this.mode = 'selected';
                this.error = 'The full graph could not be loaded. Showing the selected segments.';
                this.rebuild();
            } else {
                this.degradeToSelection();
            }
        } finally {
            this.loading = false;
        }
    }

    /**
     * Rebuild from the cached flows for the current scope. The headline coverage comes from all selected
     * flows; the drawn graph is narrowed to the coverage states the legend currently has toggled on.
     */
    private rebuild(): void {
        const flows = this.flowCache.get(this.mode);
        if (!flows) return;
        const states = segmentStates(flows, this.data.pairs, this.data.services);
        const allVisible = this.visibleStates.size === 3 && this.showNotSelected;
        // The drawn graph is narrowed to the states toggled on in the legend; its raw Sankey index is kept
        // so a hover/click resolves to its flows. Ribbons render proportionally (green covered + neutral
        // excluded bands sized by circuits); partial *nodes* read amber-hatched.
        const displayFlows = allVisible
            ? flows
            : flows.filter((f) => {
                  const state = states.get(flowPairKey(f));
                  // Not-selected context flows (no segment state; only present in the Full graph) are the
                  // faded "Not selected" layer, governed by its own toggle rather than the coverage states.
                  return state === undefined ? this.showNotSelected : this.visibleStates.has(state);
              });
        this.displayData = buildTrafficSankey(displayFlows, { expandedIdentities: this.expandedIdentities });
        const graph = buildCoverageGraph(this.displayData, this.data.pairs, this.data.services, 'proportional');
        // The headline coverage is over ALL selected flows, not the filtered subset.
        const full = allVisible ? graph : coverageGraphFromFlows(flows, this.data.pairs, this.data.services);
        this.coveredCircuits = full.coveredCircuits;
        this.selectedCircuits = full.selectedCircuits;
        // A mode/visibility change invalidates whatever element was open.
        this.clearDetail();
        this.apply(this.mode, graph);
    }

    private apply(mode: Mode, graph: CoverageGraph): void {
        if (mode === 'selected') {
            const iso = isolateSelected(graph);
            this.nodes = iso.nodes;
            this.links = iso.links;
            this.headers = iso.headers;
            this.baseHighlightNodes = [];
            this.baseHighlightLinks = [];
        } else {
            // Full graph: feed everything and highlight the selection so the rest fades.
            this.nodes = graph.nodes;
            this.links = graph.links;
            this.headers = graph.headers;
            this.baseHighlightNodes = graph.selectedNodeIds;
            this.baseHighlightLinks = graph.selectedLinkIds;
        }
        this.restoreHighlight();
    }

    // --- hover: light the hovered element's path, fade the rest ---------------

    onNodeHover(ev: SankeyNodeClickEvent | null): void {
        if (!ev) {
            this.restoreHighlight();
            return;
        }
        this.applyHoverHighlight(this.displayData.flowsByNode.get(ev.nodeId) ?? []);
    }

    private applyHoverHighlight(flows: TrafficRow[]): void {
        const { nodeIds, linkIds } = highlightForFlows(this.displayData, flows);
        this.highlightNodeIds = nodeIds;
        // highlightForFlows returns base ribbon ids; the drawn ribbons are split (base#cov/#unc/#ctx), so
        // map each base id onto the split ids actually present.
        const baseSet = new Set(linkIds);
        this.highlightLinkIds = this.links.filter((l) => baseSet.has(this.baseLinkId(l.id))).map((l) => l.id);
    }

    private restoreHighlight(): void {
        this.highlightNodeIds = this.baseHighlightNodes;
        this.highlightLinkIds = this.baseHighlightLinks;
    }

    // --- click: open the coverage detail drawer ------------------------------

    onNodeClick(ev: SankeyNodeClickEvent): void {
        const resolved = flowsForNode(this.displayData, ev);
        // Always break down by source: excluding a source is the only edit scoped to one path's traffic, so
        // every element (node or ribbon) is characterized by the sources carrying its flows.
        this.openDetail(resolved.eyebrow, resolved.title, resolved.flows, 'source');
    }

    onLinkClick(ev: SankeyLinkClickEvent): void {
        // Resolve against the base ribbon id (the split suffix is only for drawing).
        const resolved = flowsForLink(this.displayData, { ...ev, linkId: this.baseLinkId(ev.linkId) });
        this.openDetail(resolved.eyebrow, resolved.title, resolved.flows, 'source');
    }

    onBackground(): void {
        this.restoreHighlight();
        this.clearDetail();
    }

    /** Drill a source identity in (show its source addresses) or collapse it back, then recolor the graph. */
    onNodeToggle(ev: SankeyNodeToggleEvent): void {
        const value = ev.nodeId.slice(ev.nodeId.indexOf(':') + 1);
        if (ev.expanded) {
            this.expandedIdentities.add(value);
        } else {
            this.expandedIdentities.delete(value);
        }
        this.rebuild();
    }

    /** A breakdown row in the detail drawer is hovered: light that row's path in the flow-map; restore on leave. */
    onDetailRowHover(ev: { flows: TrafficRow[] } | null): void {
        if (!ev) {
            this.restoreHighlight();
            return;
        }
        this.applyHoverHighlight(ev.flows);
    }

    /** A source's include checkbox was toggled: edit the governing service(s), then recompute the graph and
     *  reopen the same panel so the meter + states reflect the change. The edits are returned on close. */
    onDetailIncludeToggle(ev: CoverageIncludeToggle): void {
        const ctx = this.detailCtx;
        if (!ctx) return;
        // A parent cascades over several items; apply each. Source rows edit admission (allowed sources);
        // endpoint rows edit the service's targets, using the whole graph's flows as the "still needed" set.
        const contextFlows = this.flowCache.get(this.mode) ?? ctx.flows;
        let changed = false;
        for (const item of ev.items) {
            if (ev.target === 'source') {
                // contextFlows (the whole graph) is the "keep everyone else" set when materializing an
                // allow-all service, so excluding this segment's source does not drop the other sources.
                if (setSourceIncluded(ctx.flows, item, ev.include, this.data.services, contextFlows)) changed = true;
            } else {
                const idx = item.lastIndexOf(':');
                const address = idx >= 0 ? item.slice(0, idx) : item;
                const port = idx >= 0 ? item.slice(idx + 1) : '';
                if (setEndpointIncluded(address, port, ev.include, this.data.services, contextFlows)) changed = true;
            }
        }
        if (!changed) return;
        this.servicesDirty = true;
        this.rebuild(); // recolors the graph + recomputes the headline against the edited services
        this.openDetail(ctx.eyebrow, ctx.title, ctx.flows, ctx.rowsBy); // refresh the open panel
    }

    closeDetail(): void {
        this.clearDetail();
    }

    private openDetail(eyebrow: string, title: string, flows: TrafficRow[], rowsBy: CoverageRowBy): void {
        const explain = explainFlows(flows, this.data.pairs, this.data.services, rowsBy);
        this.detail = { eyebrow, title, explain };
        this.detailCtx = { eyebrow, title, flows, rowsBy };
    }

    private clearDetail(): void {
        this.detail = null;
        this.detailCtx = null;
    }

    /** Strip the proportional-ribbon suffix (`<base>#cov`) back to the base ribbon id. */
    private baseLinkId(id: string): string {
        const hash = id.indexOf('#');
        return hash >= 0 ? id.slice(0, hash) : id;
    }

    private async flowsFor(mode: Mode): Promise<TrafficRow[]> {
        const cached = this.flowCache.get(mode);
        if (cached) return cached;
        if (!this.hasWindow) throw new Error('no-window');
        const network = this.apiService.currentNetwork?.value;
        if (!network?.id || !network?.networkGroupId) throw new Error('no-network');
        const start = moment(this.data.start).utc().format();
        const end = moment(this.data.end).utc().format();
        // Selected mode restricts to the selection's dest IPs/ports (small result); full mode queries the
        // whole window and never restricts the source side.
        const restrictIp = mode === 'selected' ? this.jsonArray(this.data.destIp) : '';
        const restrictPort = mode === 'selected' ? this.jsonArray(this.data.destPort) : '';
        const params = this.templateService.getTrafficAnalysisData(
            'ncfabriccircuits',
            start,
            end,
            undefined,
            undefined,
            network.id,
            network.networkGroupId,
            restrictIp,
            restrictPort,
            ''
        );
        const resp = await firstValueFrom(
            this.elasticsearch.apiTemplateSearch(this.templateService.TRAFFIC_ANALYSIS_DATA_TEMPLATE, params)
        );
        const flows = parseTrafficFlows(resp);
        this.flowCache.set(mode, flows);
        return flows;
    }

    private degradeToSelection(): void {
        this.degraded = true;
        this.hasWindow = false; // no full-graph context without the window
        this.mode = 'selected';
        const flows = buildSelectionFlows(
            this.data.pairs,
            this.data.srcIdentities,
            this.data.srcIps,
            this.data.hostIdentities
        );
        this.flowCache.set('selected', flows); // so the ribbon-style toggle still works offline
        this.rebuild();
        this.error = flows.length ? '' : 'There are no selected segments to show.';
    }

    /** Format a comma-joined list as the template's quoted JSON array; empty list -> no restriction. */
    private jsonArray(csv: string): string {
        const out = (csv || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => `"${s}"`);
        return out.length ? `[${out.join(',')}]` : '';
    }
}
